/**
 * Logger Utility
 *
 * Provides centralized logging with environment-aware behavior.
 * Debug logs only appear in development mode.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LoggerConfig {
  enableDebug: boolean
  prefix?: string
}

class Logger {
  private config: LoggerConfig

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      enableDebug: import.meta.env.DEV,
      ...config,
    }
  }

  private formatMessage(level: LogLevel, message: string): string {
    const timestamp = new Date().toISOString()
    const prefix = this.config.prefix ? `[${this.config.prefix}]` : ''
    return `${timestamp} ${prefix} [${level.toUpperCase()}] ${message}`
  }

  /**
   * Debug logs - only shown in development mode
   */
  debug(message: string, data?: unknown): void {
    if (this.config.enableDebug) {
      console.log(this.formatMessage('debug', message), data !== undefined ? data : '')
    }
  }

  /**
   * Info logs - shown in all environments
   */
  info(message: string, data?: unknown): void {
    console.info(this.formatMessage('info', message), data !== undefined ? data : '')
  }

  /**
   * Warning logs - shown in all environments
   */
  warn(message: string, data?: unknown): void {
    console.warn(this.formatMessage('warn', message), data !== undefined ? data : '')
  }

  /**
   * Error logs - shown in all environments
   */
  error(message: string, error?: unknown): void {
    console.error(this.formatMessage('error', message), error !== undefined ? error : '')
  }

  /**
   * Create a child logger with a specific prefix
   */
  child(prefix: string): Logger {
    return new Logger({
      ...this.config,
      prefix: this.config.prefix ? `${this.config.prefix}:${prefix}` : prefix,
    })
  }
}

// Export a default logger instance
export const logger = new Logger()

// Export the Logger class for creating custom instances
export default Logger
